import React from "react";
import { Link as RouterLink, useParams } from "react-router-dom";
import {
  Badge,
  Box,
  Card,
  Flex,
  Heading,
  HStack,
  Icon,
  Image,
  SimpleGrid,
  Spinner,
  Stack,
  Text,
} from "@chakra-ui/react";
import { LuArrowRight, LuCalendar, LuClock, LuMapPin } from "react-icons/lu";

import ImageGallery from "components/utils/ImageGallery";
import { Button } from "components/ui/Button";
import AppAvatar from "components/utils/AppAvatar";
import { useMainNivelActividadesPublicas } from "features/main/actividades/hooks";
import { formatFechaRange, formatHoraHHMM } from "utils/formatters";
import { resolveAvatarUrl } from "utils/avatar";

const EMPTY_MSG = "Aun no se ha planeado una actividad para este fin de semana.";
const DEFAULT_COVER = "https://images.unsplash.com/photo-1521412644187-c49fa049e84d?auto=format&fit=crop&w=1200&q=60";

function formatActividadFecha(actividad) {
  return formatFechaRange({
    fechaInicio: actividad?.fechaInicio ?? actividad?.fecha ?? null,
    fechaFin: actividad?.fechaFin ?? null,
  });
}

function formatActividadTraslado(actividad) {
  const dropOff = String(actividad?.dropOff ?? "").trim();
  const pickUp = String(actividad?.pickUp ?? "").trim();

  if (dropOff && pickUp && dropOff !== pickUp) return `Drop-off: ${dropOff} | Pick-up: ${pickUp}`;
  if (dropOff) return dropOff;
  if (pickUp) return pickUp;
  return actividad?.lugar || "Lugar por confirmar";
}

function formatNivelCampus(nivelNombre, campusNombre) {
  const nivel = String(nivelNombre || "").trim() || "Nivel";
  const campus = String(campusNombre || "").trim();
  return campus ? `${nivel} - ${campus}` : nivel;
}

function instructorRoleRank(rol) {
  const normalized = String(rol || "").trim().toLowerCase();
  if (normalized === "jefe de nivel") return 0;
  if (normalized === "subjefe de nivel") return 1;
  return 2;
}

function instructorRoleLabel(rol) {
  const normalized = String(rol || "").trim();
  if (normalized === "Jefe de Nivel") return "Jefe de grado";
  if (normalized === "Subjefe de Nivel") return "Subjefe de grado";
  return normalized || "Instructor";
}

const SectionEyebrow = ({ children }) => (
  <Text
    alignSelf="start"
    px="3"
    py="1"
    rounded="full"
    bg="public.bg.muted"
    color="public.fg.default"
    borderWidth="1px"
    borderColor="public.border.default"
    fontSize="xs"
    fontWeight="800"
    letterSpacing="wide"
    textTransform="uppercase"
  >
    <Box as="span" display="inline-block" boxSize="7px" mr="2" rounded="full" bg="brand.red" />
    {children}
  </Text>
);

const SectionHeading = ({ eyebrow, title, description, action }) => (
  <Flex align={{ base: "start", md: "end" }} justify="space-between" direction={{ base: "column", md: "row" }} gap="4" mb="7">
    <Stack gap="3">
      <SectionEyebrow>{eyebrow}</SectionEyebrow>
      <Heading as="h2" fontFamily="display" fontSize={{ base: "32px", md: "48px" }} lineHeight="1.02" color="public.fg.default">
        {title}
      </Heading>
      {description ? (
        <Text maxW="3xl" color="public.fg.muted" fontSize="md" lineHeight="1.75">
          {description}
        </Text>
      ) : null}
    </Stack>
    {action}
  </Flex>
);

const SectionShell = ({ children, bg = "public.bg.canvas", color = "public.fg.default", ...props }) => (
  <Box as="section" bg={bg} color={color} px={{ base: 5, md: 10 }} py={{ base: 12, md: 20 }} {...props}>
    <Box maxW="7xl" mx="auto">
      {children}
    </Box>
  </Box>
);

const NivelHero = ({ nivelNombre, campusNombre }) => (
  <Box as="section" position="relative" overflow="hidden" bg="public.bg.canvas" color="public.fg.default" px={{ base: 5, md: 10 }} pt={{ base: "82px", md: "116px" }} pb={{ base: 7, md: 11 }} borderBottomWidth="1px" borderColor="public.border.subtle">
    <Box position="absolute" w={{ base: "160px", md: "330px" }} h={{ base: "62px", md: "105px" }} rounded="full" bg="#dce8f1" _dark={{ bg: "#243746" }} right={{ base: "-82px", md: "7%" }} bottom={{ base: "-30px", md: "-58px" }} transform="rotate(-8deg)" opacity=".72" />
    <Stack position="relative" zIndex="1" maxW="7xl" mx="auto" gap="2">
      {campusNombre ? <Text color="brand.redFg" fontSize="xs" fontWeight="800" letterSpacing="wide" textTransform="uppercase">{campusNombre}</Text> : null}
      <Heading as="h1" fontFamily="display" fontSize={{ base: "38px", md: "56px" }} lineHeight="1" letterSpacing="-.03em">
        {nivelNombre} grado
      </Heading>
    </Stack>
  </Box>
);

const DetailItem = ({ icon, children }) => (
  <HStack align="start" gap="3" color="public.fg.muted">
    <Icon as={icon} boxSize="4" mt="0.5" color="brand.redFg" />
    <Text fontSize="sm" lineHeight="1.6">
      {children}
    </Text>
  </HStack>
);

const WeeklyActivity = ({ actividad }) => (
  <Box>
    <SectionHeading eyebrow="Actividad de la semana" title="Próxima actividad" />
    <Card.Root overflow="hidden" rounded="28px 12px 28px 12px" bg="public.bg.elevated" borderWidth="1px" borderColor="public.border.default" shadow="cardLift">
      {actividad ? (
        <SimpleGrid columns={{ base: 1, lg: 2 }}>
          <Box minH={{ base: "230px", sm: "300px", lg: "440px" }} overflow="hidden">
            <Image src={actividad.coverUrl || DEFAULT_COVER} alt={actividad.titulo} w="full" h="full" objectFit="cover" />
          </Box>
          <Card.Body p={{ base: 5, md: 10 }} justifyContent="center" gap="5">
            <Heading as="h3" fontFamily="display" fontSize={{ base: "3xl", md: "4xl" }} lineHeight="1.05">
              {actividad.titulo}
            </Heading>
            <Stack gap="3">
              <DetailItem icon={LuCalendar}>{formatActividadFecha(actividad)}</DetailItem>
              <DetailItem icon={LuClock}>
                {formatHoraHHMM(actividad.horaInicio)} - {formatHoraHHMM(actividad.horaFin)}
              </DetailItem>
              <DetailItem icon={LuMapPin}>{formatActividadTraslado(actividad)}</DetailItem>
            </Stack>
            <Text color="public.fg.muted" lineHeight="1.8">
              {actividad.descripcion || "Sin descripcion."}
            </Text>
          </Card.Body>
        </SimpleGrid>
      ) : (
        <Card.Body p={{ base: 8, md: 12 }}>
          <Text textAlign="center" color="public.fg.muted">
            {EMPTY_MSG}
          </Text>
        </Card.Body>
      )}
    </Card.Root>
  </Box>
);

const PreviousActivityCard = ({ actividad }) => (
  <Card.Root
    role="group"
    overflow="hidden"
    rounded="24px 10px 24px 10px"
    bg="public.bg.elevated"
    borderWidth="1px"
    borderColor="public.border.default"
    w="full"
    minW={{ md: "340px" }}
    maxW={{ md: "380px" }}
    display="grid"
    gridTemplateColumns={{ base: "112px minmax(0, 1fr)", md: "1fr" }}
    transition="transform 180ms ease, box-shadow 180ms ease"
    _hover={{ transform: "translateY(-4px)", shadow: "cardLift" }}
  >
    <Box position="relative" h={{ base: "full", md: "220px" }} minH={{ base: "148px", md: "auto" }} overflow="hidden">
      <Image
        src={actividad.coverUrl || DEFAULT_COVER}
        alt={actividad.titulo}
        w="full"
        h="full"
        objectFit="cover"
        transition="transform 500ms ease"
        _groupHover={{ transform: "scale(1.06)" }}
      />
      <Box position="absolute" inset="0" bg="linear-gradient(to top, rgba(10,0,0,.72), transparent 64%)" />
      <Badge display={{ base: "none", md: "block" }} position="absolute" left="4" top="4" bg="whiteAlpha.900" color="gray.900" rounded="full" px="3">
        {formatActividadFecha(actividad)}
      </Badge>
    </Box>
    <Card.Body p={{ base: 4, md: 5 }} gap={{ base: 2, md: 3 }} minW="0">
      <Badge display={{ base: "block", md: "none" }} bg="public.bg.muted" color="public.fg.muted" rounded="full" px="2.5" py=".5" alignSelf="start" fontSize="10px">
        {formatActividadFecha(actividad)}
      </Badge>
      <Heading as="h3" fontFamily="display" fontSize={{ base: "lg", md: "2xl" }} lineHeight="1.1" lineClamp="2">
        {actividad.titulo}
      </Heading>
      <Stack gap={{ base: 1, md: 2 }}>
        <DetailItem icon={LuClock}>
          {formatHoraHHMM(actividad.horaInicio)} - {formatHoraHHMM(actividad.horaFin)}
        </DetailItem>
        <DetailItem icon={LuMapPin}>{formatActividadTraslado(actividad)}</DetailItem>
      </Stack>
    </Card.Body>
  </Card.Root>
);

const PreviousActivities = ({ actividades }) => (
  <Box>
    <SectionHeading eyebrow="Historial" title="Actividades anteriores" />
    {actividades.length ? (
      <Flex direction={{ base: "column", md: "row" }} gap={{ base: 3, md: 5 }} overflowX={{ md: "auto" }} align="stretch" pb="3">
        {actividades.map((actividad) => (
          <PreviousActivityCard key={actividad.actividadId} actividad={actividad} />
        ))}
      </Flex>
    ) : (
      <Card.Root bg="public.bg.elevated" borderWidth="1px" borderColor="public.border.default" rounded="24px">
        <Card.Body p="8">
          <Text textAlign="center" color="public.fg.muted">
            No hay actividades anteriores en este periodo.
          </Text>
        </Card.Body>
      </Card.Root>
    )}
  </Box>
);

const InstructorCard = ({ instructor }) => {
  const rank = instructorRoleRank(instructor.rol);
  const isLead = rank === 0;
  const isSublead = rank === 1;
  const label = instructorRoleLabel(instructor.rol);

  return (
    <Card.Root
      bg="public.bg.elevated"
      borderWidth="1px"
      borderColor={isLead ? "brand.redBorder" : isSublead ? "orange.400" : "public.border.default"}
      rounded={{ base: "18px", md: "24px" }}
      h="full"
      transition="transform 180ms ease, box-shadow 180ms ease"
      _hover={{ transform: "translateY(-4px)", shadow: "cardLift" }}
    >
      <Card.Body p={{ base: 3, md: 5 }} gap={{ base: 2, md: 4 }} direction="column" alignItems="center" textAlign="center">
        <AppAvatar display={{ base: "flex", md: "none" }} size={48} name={instructor.nombre} src={resolveAvatarUrl(instructor)} seed={instructor.instructorId || instructor.nombre} />
        <AppAvatar display={{ base: "none", md: "flex" }} size="2xl" name={instructor.nombre} src={resolveAvatarUrl(instructor)} seed={instructor.instructorId || instructor.nombre} />
        <Stack gap="1" minW="0" flex="1">
          <Heading as="h3" fontFamily="display" fontSize={{ base: "sm", md: "xl" }} lineHeight="1.15" lineClamp="2">
            {instructor.nombre}
          </Heading>
          <Text color={isLead ? "brand.redFg" : isSublead ? "orange.700" : "public.fg.muted"} _dark={{ color: isLead ? "brand.redFg" : isSublead ? "orange.300" : "public.fg.muted" }} fontSize={{ base: "xs", md: "sm" }} fontWeight="800" truncate>
            {label}
          </Text>
        </Stack>
        {isLead || isSublead ? (
          <Badge flexShrink="0" bg={isLead ? "brand.red" : "orange.600"} color="white" rounded="full" px={{ base: 2, md: 3 }} fontSize={{ base: "9px", md: "xs" }}>
            {isLead ? "Jefe" : "Subjefe"}
          </Badge>
        ) : null}
      </Card.Body>
    </Card.Root>
  );
};

const Instructors = ({ instructores }) => (
  <Box>
    <SectionHeading
      eyebrow="El equipo"
      title="Instructores del grado"
      description="Conoce a los instructores que organizan las actividades"
    />
    {instructores.length ? (
      <SimpleGrid columns={{ base: 2, md: 2, lg: 4 }} gap={{ base: 2.5, md: 5 }}>
        {instructores.map((instructor) => (
          <InstructorCard key={instructor.instructorId || instructor.nombre} instructor={instructor} />
        ))}
      </SimpleGrid>
    ) : (
      <Card.Root bg="public.bg.elevated" borderWidth="1px" borderColor="public.border.default" rounded="24px">
        <Card.Body p="8">
          <Text textAlign="center" color="public.fg.muted">
            No hay instructores registrados para este periodo.
          </Text>
        </Card.Body>
      </Card.Root>
    )}
  </Box>
);

const GradeCta = ({ nivelNombre, campusNombre }) => (
  <Box bg="public.bg.canvas" px={{ base: 5, md: 10 }} py={{ base: 12, md: 20 }}>
    <Box bg="gray.950" color="white" position="relative" overflow="hidden" maxW="6xl" mx="auto" rounded="40px 16px 40px 16px">
    <Box position="absolute" inset="0" bgImage="radial-gradient(circle, rgba(255,255,255,0.12) 1px, transparent 1px)" bgSize="28px 28px" />
    <Box position="relative" zIndex="1" px={{ base: 6, md: 10 }} py={{ base: 12, md: 16 }}>
      <Flex align={{ base: "start", md: "center" }} justify="space-between" direction={{ base: "column", md: "row" }} gap="8">
        <Stack gap="2">
          <Heading as="h2" fontFamily="display" fontSize={{ base: "3xl", md: "5xl" }} lineHeight="1.02">
            ¿Tu hijo es del grado?
          </Heading>
          <Text color="whiteAlpha.900" fontSize={{ base: "md", md: "lg" }} lineHeight="1.7" maxW="2xl">
            Regístralo para recibir información de actividades, campamentos y eventos de {formatNivelCampus(nivelNombre, campusNombre)}.
          </Text>
        </Stack>
        <Button as={RouterLink} to="/registro" variant="ghost" color="transparent" size="lg">
          Registrar a mi hijo
          <Icon as={LuArrowRight} boxSize="4" />
        </Button>
      </Flex>
    </Box></Box>
  </Box>
);

const Nivel = () => {
  const { id } = useParams();
  const q = useMainNivelActividadesPublicas(id);

  const nivelNombre = q.data?.nivel?.nivelNombre || "Nivel";
  const campusNombre = q.data?.nivel?.campusNombre || "";
  const actividadPrincipal = q.data?.actividadPrincipal || null;
  const actividadesAnteriores = Array.isArray(q.data?.actividadesAnteriores) ? q.data.actividadesAnteriores : [];
  const instructores = React.useMemo(() => {
    const rows = Array.isArray(q.data?.instructores) ? q.data.instructores : [];
    return [...rows].sort((a, b) => {
      const byRole = instructorRoleRank(a?.rol) - instructorRoleRank(b?.rol);
      if (byRole) return byRole;
      return String(a?.nombre || "").localeCompare(String(b?.nombre || ""));
    });
  }, [q.data?.instructores]);
  const galeria = Array.isArray(q.data?.galeria) ? q.data.galeria : [];
  const galeriaUrls = galeria.map((g) => g.downloadUrl).filter(Boolean);
  return (
    <Box bg="public.bg.canvas" color="public.fg.default">
      <NivelHero nivelNombre={nivelNombre} campusNombre={campusNombre} />

      {q.isLoading ? (
        <Flex justify="center" py="16">
          <Spinner size="xl" color="brand.red" />
        </Flex>
      ) : null}

      {q.isError ? (
        <Text textAlign="center" color="red.500" py="10">
          No fue posible cargar la informacion del nivel.
        </Text>
      ) : null}

      {!q.isLoading && !q.isError ? (
        <>
          <SectionShell bg="public.bg.canvas">
            <WeeklyActivity actividad={actividadPrincipal} />
          </SectionShell>
          <SectionShell bg="public.bg.panel">
            <Instructors instructores={instructores} />
          </SectionShell>
          <SectionShell bg="public.bg.canvas">
            <PreviousActivities actividades={actividadesAnteriores} />
          </SectionShell>

          <SectionShell bg="public.bg.panel">
            <SectionHeading eyebrow="Galería de fotos" title="Momentos del grado" />
            <ImageGallery images={galeriaUrls} />
          </SectionShell>

          <GradeCta nivelNombre={nivelNombre} campusNombre={campusNombre} />
        </>
      ) : null}
    </Box>
  );
};

export default Nivel;
